import {Injectable} from '@angular/core';
import CryptoJS from 'crypto-js';
import {environment} from "../../environments/environment.dev";

@Injectable({
    providedIn: 'root'
})
export class AesService {
    private readonly key: CryptoJS.lib.WordArray;
    private readonly blockSize = 16; // AES block size is 16 bytes (128 bits)

    constructor() {
        // Parse the key (should match ainstitute_env.iam_key in Python)
        this.key = CryptoJS.enc.Base64.parse(environment.erpKey);
    }

    encrypt(data: any): string {
        try {
            // 1. Convert data to JSON string
            const jsonString = JSON.stringify(data);

            // 2. Generate random IV (16 bytes)
            const iv = CryptoJS.lib.WordArray.random(this.blockSize);

            // 3. Encrypt with AES-CBC with PKCS7 padding
            const encrypted = CryptoJS.AES.encrypt(
                jsonString,
                this.key,
                {
                    iv: iv,
                    mode: CryptoJS.mode.CBC,
                    padding: CryptoJS.pad.Pkcs7
                }
            );

            // 4. Combine IV and ciphertext
            const ivAndCiphertext = iv.concat(CryptoJS.enc.Base64.parse(encrypted.toString()));

            // 5. Convert to hex string
            return ivAndCiphertext.toString(CryptoJS.enc.Hex);
        } catch (error) {
            console.error('Encryption error:', error);
            throw new Error('Encryption failed');
        }
    }

    decrypt(encryptedHex: string): any {
        try {
            if (!encryptedHex || typeof encryptedHex !== 'string') {
                throw new Error('Invalid encrypted data');
            }

            // 1. Convert hex string to bytes
            const encryptedData = CryptoJS.enc.Hex.parse(encryptedHex);

            // 2. Extract IV (first 16 bytes)
            const iv = CryptoJS.lib.WordArray.create(encryptedData.words.slice(0, this.blockSize / 4));

            // 3. Extract ciphertext (remaining bytes)
            const ciphertext = CryptoJS.lib.WordArray.create(encryptedData.words.slice(this.blockSize / 4));

            // 4. Decrypt with AES-CBC with PKCS7 padding
            const decrypted = CryptoJS.AES.decrypt(
                {ciphertext: ciphertext} as any,
                this.key,
                {
                    iv: iv,
                    mode: CryptoJS.mode.CBC,
                    padding: CryptoJS.pad.Pkcs7
                }
            );

            // 5. Convert to UTF-8 string
            const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);

            if (!decryptedStr) {
                throw new Error('Decryption failed - empty result');
            }

            // Debug output before JSON parsing
            console.debug('Decrypted string:', decryptedStr);

            // 6. Parse JSON with error handling
            try {
                return JSON.parse(decryptedStr);
            } catch (jsonError) {
                console.error('JSON parsing error:', jsonError);
                console.error('Problematic decrypted content:', decryptedStr);
                throw new Error('Failed to parse decrypted data as JSON');
            }
        } catch (error) {
            console.error('Decryption error:', error);
            throw error; // Re-throw to let caller handle it
        }
    }
}
